import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { translateError, useT } from "../i18n/hooks.ts";
import { PARAMETER_DEFINITIONS } from "../model/baseTemplate.ts";
import { calibrationRangeFor } from "../model/calibrationParameterRanges.ts";
import type { DailySeries, ProjectSeries } from "../model/dailySeries.ts";
import { renameOutletPoint, updateParameterValue } from "../model/projectEntities.ts";
import type { IndicatorCode } from "../engine/calibration/objectiveFunction.ts";
import type { EvalHidProject } from "../model/types.ts";
import { activeUnitName } from "../model/units.ts";
import {
  alignToDateAxis,
  runCalibration,
  type CalibrationRunResult,
  type WarmUpUnit,
} from "../engine/calibration/runCalibration.ts";
import { InfoIcon } from "./InfoIcon.tsx";
import { EditOutletPointDialog } from "./OutletPointCatalog.tsx";

/** Below this many valid observed days, a window (calibration OR validation) is too sparse to trust — Launch stays disabled until both clear it independently. */
const MIN_VALID_OVERLAP = 10;

const CHART_COLOR = "#3fa7b3";

/** All 6, in a fixed report order — see runCalibration.ts's REPORT_WEIGHTS. */
const REPORT_INDICATOR_CODES: IndicatorCode[] = [1, 2, 3, 4, 5, 6];

export function CalibrationPanel({
  project,
  series,
  observedFlow,
  onChangeProject,
  onChangeObservedFlow,
  onClose,
}: {
  project: EvalHidProject;
  series: ProjectSeries;
  observedFlow: DailySeries | null;
  onChangeProject: (project: EvalHidProject) => void;
  onChangeObservedFlow: (series: DailySeries | null) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [outletCode, setOutletCode] = useState<number | null>(null);
  const [zoneCode, setZoneCode] = useState<number | null>(null);
  const [parameterCodes, setParameterCodes] = useState<Set<number>>(new Set());
  /** Calibration [calibrationStart, calibrationEnd] and validation [validationStart, validationEnd] — two fully independent windows, not a shared split point. Defaulted to the two halves of the whole precipitation range when an outlet point is picked; freely editable afterwards, including into overlapping or non-adjacent ranges. */
  const [calibrationStart, setCalibrationStart] = useState("");
  const [calibrationEnd, setCalibrationEnd] = useState("");
  const [validationStart, setValidationStart] = useState("");
  const [validationEnd, setValidationEnd] = useState("");
  /** Extra lead-in period simulated (but never scored) before EACH window's own start — see runCalibration.ts's `CalibrationDateRange.warmUp`. 0: no warm-up, same as before this existed. */
  const [warmUpAmount, setWarmUpAmount] = useState(0);
  const [warmUpUnit, setWarmUpUnit] = useState<WarmUpUnit>("days");
  const [running, setRunning] = useState(false);
  const [progressHistory, setProgressHistory] = useState<
    { evaluations: number; bestValue: number }[]
  >([]);
  const [result, setResult] = useState<CalibrationRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Which outlet point's edit dialog to open directly from the empty state below — see its own comment. */
  const [editingOutletCode, setEditingOutletCode] = useState<number | null>(null);

  const outletsWithObserved = useMemo(
    () => project.outletPoints.filter((p) => observedFlow?.columns.includes(p.name)),
    [project.outletPoints, observedFlow]
  );

  const zonesForOutlet = useMemo(() => {
    if (outletCode == null) return [];
    const zoneCodes = new Set(
      project.subBasins.filter((sb) => sb.outletCode === outletCode).map((sb) => sb.zoneCode)
    );
    return project.zones.filter((z) => zoneCodes.has(z.zoneCode) && z.modelTypeCode != null);
  }, [project.subBasins, project.zones, outletCode]);

  const zone = zoneCode != null ? project.zones.find((z) => z.zoneCode === zoneCode) : undefined;
  const paramsForZone = useMemo(
    () =>
      zone?.modelTypeCode != null
        ? PARAMETER_DEFINITIONS.filter(
            (d) => d.modelTypeCode === zone.modelTypeCode && d.kind === 0
          )
        : [],
    [zone]
  );

  const observedAligned = useMemo(() => {
    if (outletCode == null || !observedFlow || !series.precipitation) return null;
    const outletPoint = project.outletPoints.find((p) => p.outletCode === outletCode);
    if (!outletPoint) return null;
    const columnIndex = observedFlow.columns.indexOf(outletPoint.name);
    if (columnIndex < 0) return null;
    return alignToDateAxis(
      observedFlow.dates,
      observedFlow.values[columnIndex],
      series.precipitation.dates
    );
  }, [outletCode, observedFlow, series.precipitation, project.outletPoints]);

  const precipDates = series.precipitation?.dates ?? [];
  const dateRangeBounds =
    precipDates.length > 0
      ? { min: precipDates[0], max: precipDates[precipDates.length - 1] }
      : null;
  const calibrationDatesChosen = calibrationStart !== "" && calibrationEnd !== "";
  const calibrationDatesInOrder = calibrationDatesChosen && calibrationStart <= calibrationEnd;
  const validationDatesChosen = validationStart !== "" && validationEnd !== "";
  const validationDatesInOrder = validationDatesChosen && validationStart <= validationEnd;

  /** Count of non-gap observed points strictly within `[startDate, endDate]` (inclusive) of the precipitation date axis — 0 whenever a date isn't on that axis, so an out-of-range or empty picker just reads as "no valid data" rather than throwing. */
  function validCountInRange(startDate: string, endDate: string): number {
    if (!observedAligned) return 0;
    const startIndex = precipDates.indexOf(startDate);
    const endIndex = precipDates.indexOf(endDate);
    if (startIndex < 0 || endIndex < 0 || startIndex > endIndex) return 0;
    return observedAligned.slice(startIndex, endIndex + 1).filter((v) => !Number.isNaN(v)).length;
  }

  const calibrationValidCount = calibrationDatesInOrder
    ? validCountInRange(calibrationStart, calibrationEnd)
    : 0;
  const validationValidCount = validationDatesInOrder
    ? validCountInRange(validationStart, validationEnd)
    : 0;

  const canLaunch =
    outletCode != null &&
    zoneCode != null &&
    parameterCodes.size > 0 &&
    calibrationDatesInOrder &&
    validationDatesInOrder &&
    calibrationValidCount >= MIN_VALID_OVERLAP &&
    validationValidCount >= MIN_VALID_OVERLAP &&
    series.precipitation != null &&
    series.etp != null &&
    !running;

  function toggleParameter(code: number) {
    setParameterCodes((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function launch() {
    if (!canLaunch || !series.precipitation || !series.etp || !observedFlow) return;
    setRunning(true);
    setResult(null);
    setError(null);
    setProgressHistory([]);
    try {
      const r = await runCalibration(
        project,
        { precipitation: series.precipitation, etp: series.etp, temperature: series.temperature },
        observedFlow,
        { outletCode: outletCode!, zoneCode: zoneCode!, parameterCodes: [...parameterCodes] },
        {
          dateRange: {
            calibrationStart,
            calibrationEnd,
            validationStart,
            validationEnd,
            warmUp: warmUpAmount > 0 ? { amount: warmUpAmount, unit: warmUpUnit } : null,
          },
          onImprovement: (evaluations, bestValue) =>
            setProgressHistory((current) => [...current, { evaluations, bestValue }]),
        }
      );
      setResult(r);
    } catch (err) {
      setError(translateError(err, t));
    } finally {
      setRunning(false);
    }
  }

  function applyResult() {
    if (!result || zoneCode == null) return;
    let updated = project;
    for (const [code, value] of Object.entries(result.bestParameterValues)) {
      updated = updateParameterValue(updated, zoneCode, Number(code), 0, value);
    }
    onChangeProject(updated);
    setResult(null);
    setProgressHistory([]);
  }

  return (
    <aside className="edit-panel">
      <div className="edit-panel-header">
        <h2>{t("calibration.title")}</h2>
        <button className="button-icon" onClick={onClose} aria-label={t("common.close")}>
          ✕
        </button>
      </div>

      <div className="edit-panel-body">
        {outletsWithObserved.length === 0 ? (
          project.outletPoints.length === 0 ? (
            <p className="muted-text">{t("calibration.noOutletPointsInProject")}</p>
          ) : (
            <>
              <p className="muted-text">{t("calibration.noPointsWithObservedSeries")}</p>
              <div className="step-mapping">
                {project.outletPoints.map((p) => (
                  <button
                    key={p.outletCode}
                    className="button"
                    onClick={() => setEditingOutletCode(p.outletCode)}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </>
          )
        ) : (
          <>
            <div className="assigner-controls">
              <label>{t("calibration.outletPoint")}</label>
              <select
                value={outletCode ?? ""}
                onChange={(e) => {
                  setOutletCode(e.target.value ? Number(e.target.value) : null);
                  setZoneCode(null);
                  setParameterCodes(new Set());
                  setResult(null);
                  setProgressHistory([]);
                  if (precipDates.length > 0) {
                    const mid = precipDates[Math.floor(precipDates.length / 2)];
                    setCalibrationStart(precipDates[0]);
                    setCalibrationEnd(mid);
                    setValidationStart(mid);
                    setValidationEnd(precipDates[precipDates.length - 1]);
                  }
                }}
              >
                <option value="">{t("common.outletPointPlaceholder")}</option>
                {outletsWithObserved.map((p) => (
                  <option key={p.outletCode} value={p.outletCode}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {outletCode != null && (
              <p className="muted-text">
                {t("calibration.unitWarning", { unit: activeUnitName(project) })}
              </p>
            )}

            {outletCode != null && (
              <div className="assigner-controls">
                <label>{t("calibration.zone")}</label>
                <select
                  value={zoneCode ?? ""}
                  onChange={(e) => {
                    setZoneCode(e.target.value ? Number(e.target.value) : null);
                    setParameterCodes(new Set());
                    setResult(null);
                    setProgressHistory([]);
                  }}
                  disabled={zonesForOutlet.length === 0}
                >
                  <option value="">{t("common.choose")}</option>
                  {zonesForOutlet.map((z) => (
                    <option key={z.zoneCode} value={z.zoneCode}>
                      {z.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {outletCode != null && zonesForOutlet.length === 0 && (
              <p className="muted-text">{t("calibration.noZonesWithModel")}</p>
            )}

            {zoneCode != null && (
              <>
                <h4>{t("calibration.parameters")}</h4>
                <div className="step-mapping">
                  {paramsForZone.map((def) => {
                    const range = calibrationRangeFor(def);
                    return (
                      <label key={def.parameterCode}>
                        <input
                          type="checkbox"
                          checked={parameterCodes.has(def.parameterCode)}
                          onChange={() => toggleParameter(def.parameterCode)}
                        />
                        {def.name}
                        {range &&
                          ` (${range.min} – ${range.max}${def.units ? ` ${def.units}` : ""})`}
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            {outletCode != null && dateRangeBounds && (
              <>
                <h4>{t("calibration.calibrationPeriodTitle")}</h4>
                <div className="assigner-controls">
                  <label>{t("calibration.calibrationStart")}</label>
                  <input
                    type="date"
                    min={dateRangeBounds.min}
                    max={dateRangeBounds.max}
                    value={calibrationStart}
                    onChange={(e) => {
                      setCalibrationStart(e.target.value);
                      setResult(null);
                      setProgressHistory([]);
                    }}
                  />
                </div>
                <div className="assigner-controls">
                  <label>{t("calibration.calibrationEnd")}</label>
                  <input
                    type="date"
                    min={dateRangeBounds.min}
                    max={dateRangeBounds.max}
                    value={calibrationEnd}
                    onChange={(e) => {
                      setCalibrationEnd(e.target.value);
                      setResult(null);
                      setProgressHistory([]);
                    }}
                  />
                </div>
                {calibrationDatesChosen && !calibrationDatesInOrder && (
                  <p className="error">{t("calibration.calibrationDatesOutOfOrder")}</p>
                )}
                {calibrationDatesInOrder && calibrationValidCount < MIN_VALID_OVERLAP && (
                  <p className="error">
                    {t("calibration.insufficientCalibrationData", {
                      n: calibrationValidCount,
                      min: MIN_VALID_OVERLAP,
                    })}
                  </p>
                )}

                <h4>{t("calibration.validationPeriodTitle")}</h4>
                <div className="assigner-controls">
                  <label>{t("calibration.validationStart")}</label>
                  <input
                    type="date"
                    min={dateRangeBounds.min}
                    max={dateRangeBounds.max}
                    value={validationStart}
                    onChange={(e) => {
                      setValidationStart(e.target.value);
                      setResult(null);
                      setProgressHistory([]);
                    }}
                  />
                </div>
                <div className="assigner-controls">
                  <label>{t("calibration.validationEnd")}</label>
                  <input
                    type="date"
                    min={dateRangeBounds.min}
                    max={dateRangeBounds.max}
                    value={validationEnd}
                    onChange={(e) => {
                      setValidationEnd(e.target.value);
                      setResult(null);
                      setProgressHistory([]);
                    }}
                  />
                </div>
                {validationDatesChosen && !validationDatesInOrder && (
                  <p className="error">{t("calibration.validationDatesOutOfOrder")}</p>
                )}
                {validationDatesInOrder && validationValidCount < MIN_VALID_OVERLAP && (
                  <p className="error">
                    {t("calibration.insufficientValidationData", {
                      n: validationValidCount,
                      min: MIN_VALID_OVERLAP,
                    })}
                  </p>
                )}

                <h4>
                  {t("calibration.warmUpTitle")} <InfoIcon text={t("calibration.warmUpHelp")} />
                </h4>
                <div className="assigner-controls">
                  <label>{t("calibration.warmUpAmount")}</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={warmUpAmount}
                    onChange={(e) => {
                      setWarmUpAmount(Math.max(0, Math.floor(Number(e.target.value) || 0)));
                      setResult(null);
                      setProgressHistory([]);
                    }}
                  />
                  <select
                    value={warmUpUnit}
                    onChange={(e) => {
                      setWarmUpUnit(e.target.value as WarmUpUnit);
                      setResult(null);
                      setProgressHistory([]);
                    }}
                  >
                    <option value="days">{t("calibration.warmUpUnit.days")}</option>
                    <option value="weeks">{t("calibration.warmUpUnit.weeks")}</option>
                    <option value="months">{t("calibration.warmUpUnit.months")}</option>
                    <option value="years">{t("calibration.warmUpUnit.years")}</option>
                  </select>
                </div>
              </>
            )}

            {(!series.precipitation || !series.etp) && (
              <p className="error">{t("calibration.missingSeries")}</p>
            )}

            {error && <p className="error">{error}</p>}

            <div className="paste-series-buttons" style={{ marginTop: "1rem" }}>
              <button className="button button-primary" onClick={launch} disabled={!canLaunch}>
                {running ? t("calibration.calculating") : t("calibration.launch")}
              </button>
            </div>

            {(running || result) && progressHistory.length > 0 && (
              <div style={{ marginTop: "1rem" }}>
                <h4>{t("calibration.progressTitle")}</h4>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={progressHistory}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="evaluations" tick={{ fontSize: 11 }} />
                    <YAxis width={50} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line
                      type="stepAfter"
                      dataKey="bestValue"
                      stroke={CHART_COLOR}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {result && (
              <div style={{ marginTop: "1rem" }}>
                <p className="muted-text">
                  {t(`calibration.stopReason.${result.stopReason}`)} (
                  {t("calibration.evaluations", { n: result.evaluations })})
                </p>

                {result.calibrationWarmUpClamped && (
                  <p className="muted-text">
                    {t("calibration.warmUpClampedCalibration", { n: result.calibrationWarmUpDays })}
                  </p>
                )}
                {result.validationWarmUpClamped && (
                  <p className="muted-text">
                    {t("calibration.warmUpClampedValidation", { n: result.validationWarmUpDays })}
                  </p>
                )}

                <table>
                  <thead>
                    <tr>
                      <th>{t("calibration.indicatorsTable.indicatorHeader")}</th>
                      <th>{t("calibration.indicatorsTable.initialHeader")}</th>
                      <th>{t("calibration.indicatorsTable.finalHeader")}</th>
                      <th>{t("calibration.indicatorsTable.validationHeader")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {REPORT_INDICATOR_CODES.map((code) => (
                      <tr key={code}>
                        <td>{t(`calibration.indicator.${code}`)}</td>
                        <td>{result.initialIndicators[code]?.toFixed(3) ?? "—"}</td>
                        <td>{result.bestIndicators[code]?.toFixed(3) ?? "—"}</td>
                        <td>{result.validationIndicators[code]?.toFixed(3) ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="paste-series-buttons" style={{ marginTop: "0.75rem" }}>
                  <button className="button button-primary" onClick={applyResult}>
                    {t("calibration.applyParameters")}
                  </button>
                  <button
                    className="button"
                    onClick={() => {
                      setResult(null);
                      setProgressHistory([]);
                    }}
                  >
                    {t("calibration.discard")}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {editingOutletCode != null &&
        (() => {
          const editingOutlet = project.outletPoints.find(
            (p) => p.outletCode === editingOutletCode
          );
          if (!editingOutlet) return null;
          return (
            <EditOutletPointDialog
              initialName={editingOutlet.name}
              onSave={(name) => {
                onChangeProject(renameOutletPoint(project, editingOutletCode, name));
                setEditingOutletCode(null);
              }}
              onClose={() => setEditingOutletCode(null)}
              observedFlow={observedFlow}
              onChangeObservedFlow={onChangeObservedFlow}
              scale={project.seriesScale}
            />
          );
        })()}
    </aside>
  );
}
